import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { styles, css, statusColor, trackEvent, setPageMeta } from "./shared";
import Nav from "./Nav";
import Footer from "./Footer";

export default function Home() {
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", property: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const recaptchaBoxRef = useRef(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    setPageMeta({
      title: "Nekretnine u Španiji — Stanovi na prodaju na Costa del Sol obali",
      description: "Nekretnine u Španiji za kupce iz Srbije — stanovi na prodaju u Málagi i na Costa del Sol obali. Ekskluzivni projekti: Evoque, Lomas 2, Unique Infinity, Organic Higuerón. Kompletna podrška na srpskom jeziku, od prve informacije do useljenja.",
      path: "/",
    });
  }, []);

  useEffect(() => {
    fetch("/developments.json")
      .then(r => r.json())
      .then(data => { setDevelopments(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Arriving from a property page's "enquire" link (?property=Evoque#kontakt) —
  // prefill the form with the property name.
  useEffect(() => {
    const property = searchParams.get("property");
    if (property) {
      setFormData((f) => ({ ...f, property }));
    }
  }, [searchParams]);

  // Scrolls to whichever section the URL hash points to (#nekretnine, #onama,
  // #kontakt) — fires both on first load (direct link, e.g. from Google or a
  // shared URL) and whenever the hash changes while already on this page (Nav
  // uses <Link to="/#..."> even when we're already home, which doesn't trigger
  // the browser's native anchor-scroll). Re-runs once loading finishes too,
  // since the property grid/images loading shifts everything below it.
  useEffect(() => {
    const hash = location.hash;
    if (!hash) return;
    const id = hash.slice(1);
    const t = setTimeout(() => {
      // "auto" (instant), not "smooth" — smooth scrollIntoView silently no-ops
      // in some browser contexts, which is worse than not animating at all.
      document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [location.hash, loading]);

  // Netlify only auto-renders reCAPTCHA into the hidden static detection form —
  // it can't reach a form that React renders at runtime, so we render it ourselves
  // into our own form's placeholder, reusing the sitekey Netlify already issued.
  useEffect(() => {
    let cancelled = false;
    let pollId;

    const renderWidget = () => {
      if (cancelled || !recaptchaBoxRef.current || recaptchaBoxRef.current.hasChildNodes()) return;
      const sitekeyEl = document.querySelector("form[hidden] .g-recaptcha[data-sitekey]");
      const sitekey = sitekeyEl && sitekeyEl.getAttribute("data-sitekey");
      if (window.grecaptcha && window.grecaptcha.render && sitekey) {
        window.grecaptcha.render(recaptchaBoxRef.current, { sitekey });
        return true;
      }
      return false;
    };

    if (!renderWidget()) {
      pollId = setInterval(() => {
        if (renderWidget()) clearInterval(pollId);
      }, 400);
    }

    return () => { cancelled = true; clearInterval(pollId); };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const botField = e.target.elements["bot-field"];
    if (botField && botField.value) {
      return;
    }

    const recaptchaField = e.target.elements["g-recaptcha-response"];
    if (recaptchaField && !recaptchaField.value) {
      setRecaptchaError(true);
      return;
    }
    setRecaptchaError(false);

    const data = new FormData();
    data.append("form-name", "contact");
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("property", formData.property);
    data.append("message", formData.message);
    if (recaptchaField) data.append("g-recaptcha-response", recaptchaField.value);

    fetch("/", { method: "POST", body: data })
      .then(() => {
        trackEvent("generate_lead", { method: "contact_form", property: formData.property || "unspecified" });
        setSubmitted(true);
      })
      .catch(() => alert("Došlo je do greške, molimo pokušajte ponovo."));
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });

  return (
    <div style={styles.root}>
      <style>{css}</style>

      <Nav />

      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroBg} />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent} className="fade-in">
          <p style={styles.heroEyebrow}>PRODAJA NOVOGRADNJE · ŠPANIJA</p>
          <h1 style={styles.heroTitle}>Novi život<br />na španskoj obali</h1>
          <p style={styles.heroSub}>Pažljivo odabrani projekti novogradnje na najlepšim lokacijama španske obale — uz podršku na srpskom jeziku.</p>
          <div style={styles.heroCta}>
            <button style={styles.btnPrimary} onClick={() => scrollTo("nekretnine")}>ISTRAŽITE NEKRETNINE</button>
            <button style={styles.btnGhost} onClick={() => scrollTo("kontakt")}>KONTAKTIRAJTE NAS</button>
          </div>
        </div>
        <div style={styles.heroScroll}>
          <div className="scroll-dot" style={styles.scrollDot} />
          <span style={styles.scrollText}>SKROLUJTE</span>
        </div>
      </section>

      {/* STATS */}
      <section style={styles.stats}>
        {[["4", "Ekskluzivna projekta"], ["od 449.000€", "Početna cena"], ["1–4", "Sobe u ponudi"], ["2026–2028", "Period useljenja"]].map(([n, l]) => (
          <div key={l} style={styles.statItem}><span style={styles.statNum}>{n}</span><span style={styles.statLabel}>{l}</span></div>
        ))}
      </section>

      {/* DEVELOPMENTS */}
      <section id="nekretnine" style={styles.section} className="section-pad">
        <div style={styles.sectionHeader}>
          <p style={styles.eyebrow}>NAŠA PONUDA</p>
          <h2 style={styles.sectionTitle}>Trenutne nekretnine</h2>
          <div style={styles.titleLine} />
          <p style={{ color: "#9a9a9a", fontSize: 14, fontWeight: 500, marginTop: 18, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.08em" }}>KLIKNITE NA NEKRETNINU ZA VIŠE DETALJA</p>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9a9a9a", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.16em", fontWeight: 500, fontSize: 13 }}>UČITAVANJE...</div>
        ) : (
          <div style={styles.grid} className="properties-grid">
            {developments.map((d, i) => (
              <Link key={d.id} to={`/nekretnine/${d.slug}`} style={{ ...styles.card, animationDelay: `${i * 0.07}s` }} className="card"
                onMouseEnter={() => setActiveCard(d.id)} onMouseLeave={() => setActiveCard(null)}>
                <div style={styles.cardImgWrap}>
                  <img src={d.images[0]} alt={`${d.name} — nekretnina na prodaju, ${d.location}`} loading="lazy" style={{ ...styles.cardImg, transform: activeCard === d.id ? "scale(1.07)" : "scale(1)" }} />
                  <div style={styles.cardOverlay} />
                  <span style={{ ...styles.statusBadge, background: statusColor[d.status] || "#c9a96e" }}>{d.status}</span>
                  <div style={{ ...styles.viewMore, opacity: activeCard === d.id ? 1 : 0 }}>DETALJI</div>
                </div>
                <div style={styles.cardBody}>
                  <p style={styles.cardType}>{d.type} · {d.rooms}</p>
                  <h3 style={styles.cardName}>{d.name}</h3>
                  <p style={styles.cardLocation}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c9a96e" strokeWidth="2" style={{ marginRight: 5, flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    {d.location} · {d.view}
                  </p>
                  <div style={styles.cardFooter}>
                    <span style={styles.cardPrice}>{d.price}</span>
                    <span style={styles.cardBtn}>DETALJI →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ABOUT */}
      <section id="onama" style={styles.aboutSection} className="section-pad">
        <div style={styles.aboutGrid} className="aboutGrid">
          <div style={styles.aboutImg}>
            <img src="/images/unique-infinity/full/01.jpg" alt="Enterijer nekretnine u Španiji — Nekretnine u Španiji agencija za Srbe" loading="lazy" style={styles.aboutPhoto} />
            <div style={styles.aboutImgAccent} />
          </div>
          <div>
            <p style={styles.eyebrow}>O NAMA</p>
            <h2 style={styles.sectionTitle}>Vaš pouzdan partner za Španiju</h2>
            <div style={styles.titleLine} />
            <p style={styles.aboutPara}>Nekretnine u Španiji pomažu klijentima iz Srbije da sa poverenjem kupe nekretninu na španskoj obali. Specijalizovani smo za novogradnju i pažljivo biramo projekte koje predstavljamo — vodeći računa o kvalitetu gradnje, lokaciji i sadržajima.</p>
            <p style={styles.aboutPara}>Svaki klijent dobija personalizovanu podršku — od prve konsultacije, preko organizacije obilaska nekretnina, do pomoći oko rezervacije i praćenja procesa do useljenja.</p>
            <p style={styles.aboutPara}>Pored toga, pomažemo tokom celog procesa pribavljanja dokumentacije za rezidenciju, asistiramo u komunikaciji sa advokatima specijalizovanim za nekretnine, i pratimo vas kroz proces overe kupoprodajnog ugovora kod notara.</p>
            <div style={styles.aboutFeatures}>
              {["Ekskluzivan izbor proverenih projekata", "Podrška na srpskom jeziku od početka do kraja", "Pomoć od rezervacije do useljenja", "Dostupnost i nakon kupovine"].map((f) => (
                <div key={f} style={styles.feature}><span style={styles.featureDot} />{f}</div>
              ))}
            </div>
            <p style={{ ...styles.aboutPara, marginTop: 28 }}>Mi smo vaš lokalni agent i tu smo da vam pomognemo sa svim detaljima i pitanjima koja imate o projektima. Na primer:</p>
            <div style={styles.aboutFeatures}>
              {[
                "Orijentacija — kako sunce izlazi i zalazi na terasi",
                "Pogled — koje jedinice imaju najbolji pogled",
                "Planovi stanova — konkretni planovi za jedinice koje vas interesuju",
                "Sadržaji — predstavljanje sadržaja i mogućnosti u naselju",
              ].map((f) => (
                <div key={f} style={styles.feature}><span style={styles.featureDot} />{f}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="kontakt" style={styles.section} className="section-pad">
        <div style={styles.sectionHeader}>
          <p style={styles.eyebrow}>RAZGOVARAJMO</p>
          <h2 style={styles.sectionTitle}>Kontaktirajte nas</h2>
          <div style={styles.titleLine} />
        </div>
        <div style={styles.contactGrid} className="contactGrid">
          <div>
            <h3 style={styles.contactInfoTitle}>Stupite u kontakt</h3>
            <p style={styles.contactInfoSub}>Naš tim vam stoji na raspolaganju za sve informacije o nekretninama u Španiji. Javite nam se i odgovorićemo u najkraćem roku.</p>
            <div style={styles.contactRow}><span style={styles.contactIcon}>📍</span><span style={styles.contactRowText}>Španija</span></div>
            <div style={styles.contactRow}>
              <span style={styles.contactIcon}>📞</span>
              <a href="tel:+34658372004" style={styles.contactRowText} className="contact-link" onClick={() => trackEvent("generate_lead", { method: "phone" })}>+34 658 372 004</a>
            </div>
            <div style={styles.contactRow}>
              <span style={styles.contactIcon}>✉️</span>
              <a href="mailto:lana@empire2025.com" style={styles.contactRowText} className="contact-link" onClick={() => trackEvent("generate_lead", { method: "email" })}>lana@empire2025.com</a>
            </div>
            <div style={styles.contactRow}><span style={styles.contactIcon}>🕐</span><span style={styles.contactRowText}>Pon – Sub: 09:00 – 19:00</span></div>
          </div>
          <div style={styles.formWrap}>
            {submitted ? (
              <div style={styles.thankYou}>
                <div style={styles.thankYouIcon}>✓</div>
                <h3 style={styles.thankYouTitle}>Poruka je primljena</h3>
                <p style={styles.thankYouSub}>Hvala što ste nas kontaktirali. Naš tim će vam se javiti u najkraćem roku.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} name="contact" netlify="true" data-netlify-recaptcha="true" style={styles.form}>
                <input type="hidden" name="form-name" value="contact" />
                <p style={{ display: "none" }}>
                  <label>Ne popunjavajte ovo polje: <input name="bot-field" tabIndex="-1" autoComplete="off" /></label>
                </p>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Ime i prezime</label>
                  <input required name="name" type="text" placeholder="Vaše ime i prezime" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} className="form-input" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email adresa</label>
                  <input required name="email" type="email" placeholder="vas@email.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={styles.input} className="form-input" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Broj telefona</label>
                  <input required name="phone" type="tel" placeholder="+381 60 000 0000" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} style={styles.input} className="form-input" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nekretnina koja vas zanima</label>
                  <select name="property" value={formData.property} onChange={(e) => setFormData({ ...formData, property: e.target.value })} style={{ ...styles.input, appearance: "none" }} className="form-input">
                    <option value="">Nisam siguran/na</option>
                    {developments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Poruka</label>
                  <textarea required name="message" placeholder="Recite nam nešto o tome šta tražite — budžet, broj soba, željenu lokaciju..." rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} style={{ ...styles.input, resize: "vertical" }} className="form-input" />
                </div>
                <div ref={recaptchaBoxRef} data-netlify-recaptcha="true"></div>
                {recaptchaError && <p style={styles.recaptchaError}>Molimo potvrdite da niste robot.</p>}
                <button type="submit" style={styles.btnPrimary}>POŠALJITE PORUKU</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
